import { Link } from 'react-router';
import { 
  GuideLayout, 
  GuideSection, 
  GuideTip, 
  GuideDarkSection
} from '@/components/guides/guide-layout';

const inlineLink =
  'text-brand-600 hover:text-brand-700 font-medium underline decoration-brand-300 underline-offset-2';

export function ClaimTimelineGuide() {
  return (
    <GuideLayout
      slug="claim-timeline"
      category="Getting Started"
      categoryColor="brand"
      title="How Long Does a Claim Take?"
      description="Realistic timelines from submission to payout, and what to expect at each stage of the pothole claims process."
      readTime="3 min"
      updatedDate="2026-01-12"
      datePublished="2026-01-10"
      reviewedFor="UK law"
      relatedGuides={[
        { slug: 'what-makes-a-valid-claim', title: 'What Makes a Valid Pothole Claim?', readTime: '5 min' },
        { slug: 'who-is-responsible', title: 'Who Is Responsible for the Road?', readTime: '4 min' },
        { slug: 'understanding-rejection-reasons', title: 'Common Rejection Reasons', readTime: '6 min' },
      ]}
      atAGlance={[
        { label: 'Typical overall timeline', value: 'Most claims take 2-3 months from submission to resolution' },
        { label: 'Fastest realistic outcome', value: '4-6 weeks when the council accepts liability quickly' },
        { label: 'Acknowledgment', value: 'Most councils confirm receipt within a week' },
        { label: 'Decision', value: 'Usually arrives between weeks 4 and 8' },
        { label: 'Payment if accepted', value: '2-4 weeks after acceptance, via BACS transfer' },
      ]}
      faqs={[
        {
          question: 'What\'s the fastest a claim can be resolved?',
          answer: 'The fastest claims are typically resolved in 4-6 weeks when the council accepts liability quickly and you\'ve provided all necessary evidence. However, this is not typical — most claims take 2-3 months.',
        },
        {
          question: 'Why do some claims take longer than others?',
          answer: 'Delays can occur if the council requests additional evidence, needs to investigate the location, or is simply dealing with a high volume of claims. Some councils are much more efficient than others.',
        },
        {
          question: 'Is there a legal deadline for councils to respond?',
          answer: 'There\'s no strict legal deadline, but councils generally aim to respond within 20-40 working days. If you haven\'t heard anything after 6-8 weeks, it\'s reasonable to send a follow-up.',
        },
        {
          question: 'Can I speed up the process?',
          answer: 'The best way to speed things up is to submit a complete claim with all evidence upfront. Incomplete claims cause back-and-forth that adds weeks to the timeline.',
        },
        {
          question: 'What happens if I need to go to court?',
          answer: 'If you escalate to small claims court, add 2-4 months for the court process. However, many councils settle once you actually file the court claim, often before the hearing date.',
        },
      ]}
    >
      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        }
        title="Setting Realistic Expectations"
      >
        <p className="mb-4">
          <Link to="/guides/what-makes-a-valid-claim" className={inlineLink}>
            Pothole claims
          </Link>
          {' '}aren't instant, but they're not endless either. Understanding the typical 
          timeline helps you plan and know when to follow up. Here's what to expect at each stage.
        </p>
        <GuideTip variant="info">
          Every claim is different, and{' '}
          <Link to="/authority-ratings" className={inlineLink}>
            some councils are faster than others
          </Link>
          . The timelines below are averages based on typical UK council response patterns.
        </GuideTip>
      </GuideSection>

      <GuideDarkSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
        }
        title="Typical Timeline"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-4 p-4 bg-brand-900/50 rounded-xl border-l-4 border-accent-500">
            <div className="w-12 h-12 bg-accent-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <span className="text-accent-400 font-bold">1-2</span>
            </div>
            <div>
              <p className="text-white font-semibold mb-1">Days 1-2: Prepare & Submit</p>
              <p className="text-brand-300 text-sm">Gather evidence, complete your claim pack with Fixtyer, and submit to the council.</p>
            </div>
          </div>

          <div className="flex items-start gap-4 p-4 bg-brand-900/50 rounded-xl border-l-4 border-brand-500">
            <div className="w-12 h-12 bg-brand-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <span className="text-brand-300 font-bold">~7</span>
            </div>
            <div>
              <p className="text-white font-semibold mb-1">Week 1: Acknowledgment</p>
              <p className="text-brand-300 text-sm">Most councils send an acknowledgment email or letter within a week confirming receipt.</p>
            </div>
          </div>

          <div className="flex items-start gap-4 p-4 bg-brand-900/50 rounded-xl border-l-4 border-brand-500">
            <div className="w-12 h-12 bg-brand-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <span className="text-brand-300 font-bold">2-6</span>
            </div>
            <div>
              <p className="text-white font-semibold mb-1">Weeks 2-6: Investigation</p>
              <p className="text-brand-300 text-sm">The council reviews your evidence and checks their inspection records. May request additional information.</p>
            </div>
          </div>

          <div className="flex items-start gap-4 p-4 bg-brand-900/50 rounded-xl border-l-4 border-success-500">
            <div className="w-12 h-12 bg-success-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <span className="text-success-300 font-bold">4-8</span>
            </div>
            <div>
              <p className="text-white font-semibold mb-1">Weeks 4-8: Decision</p>
              <p className="text-brand-300 text-sm">You receive a decision — acceptance, partial offer, or rejection with reasons.</p>
            </div>
          </div>

          <div className="flex items-start gap-4 p-4 bg-brand-900/50 rounded-xl border-l-4 border-success-500">
            <div className="w-12 h-12 bg-success-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <span className="text-success-300 font-bold">+2-4</span>
            </div>
            <div>
              <p className="text-white font-semibold mb-1">If Accepted: +2-4 Weeks for Payment</p>
              <p className="text-brand-300 text-sm">After acceptance, councils typically process payment within 2-4 weeks via BACS transfer.</p>
            </div>
          </div>
        </div>
      </GuideDarkSection>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        }
        title="When to Chase"
      >
        <p className="mb-4">
          Councils are busy and claims can fall through the cracks. Here's when it's appropriate to follow up:
        </p>
        <ul className="space-y-3 mb-4">
          <li className="flex items-start gap-3">
            <svg className="w-5 h-5 text-brand-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-surface-700"><strong>No acknowledgment after 2 weeks:</strong> Check it was received</span>
          </li>
          <li className="flex items-start gap-3">
            <svg className="w-5 h-5 text-brand-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-surface-700"><strong>No decision after 6-8 weeks:</strong> Politely request an update</span>
          </li>
          <li className="flex items-start gap-3">
            <svg className="w-5 h-5 text-brand-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-surface-700"><strong>No payment after acceptance + 6 weeks:</strong> Chase the payment</span>
          </li>
        </ul>
        <GuideTip variant="success" title="Mac helps">
          Your{' '}
          <Link to="/how-it-works" className={inlineLink}>
            claim pack
          </Link>
          {' '}includes follow-up letter templates so you know exactly what to say 
          when chasing your claim.
        </GuideTip>
      </GuideSection>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
          </svg>
        }
        title="If Your Claim Is Rejected"
      >
        <p className="mb-4">
          Not all claims are accepted first time. If rejected, you have options:
        </p>
        <div className="bg-surface-50 rounded-xl p-4 mb-4 border border-surface-200">
          <p className="text-surface-700 mb-3">
            <strong className="text-brand-950">
              <Link to="/guides/small-claims-court-guide" className={inlineLink}>
                Small Claims Court
              </Link>
              {' '}Timeline:
            </strong>
          </p>
          <ul className="space-y-2 text-sm text-surface-600">
            <li>• Filing the claim: 1-2 days</li>
            <li>• Council has 14 days to respond</li>
            <li>
              • If defended: 8-12 weeks to{' '}
              <Link to="/guides/what-to-expect-at-hearing" className={inlineLink}>
                hearing
              </Link>
            </li>
            <li>• Many councils settle before hearing</li>
          </ul>
        </div>
        <GuideTip variant="info">
          Don't give up if rejected — see the guide on{' '}
          <Link to="/guides/understanding-rejection-reasons" className={inlineLink}>
            Common Rejection Reasons
          </Link>
          {' '}to understand whether escalation is worthwhile.
        </GuideTip>
      </GuideSection>
    </GuideLayout>
  );
}
